import {
  BcItem,
  formatItemVariant,
  Item,
  ItemVariant,
  PriceList,
  PriceSheetRow,
  ShopifyProduct,
  ShopifyPrice,
  ShopifyProductVariant,
} from "@megaron/catalog-contracts";
import { DiffSyncPage, DiffSyncRecord } from "@megaron/diff-sync-webapp";
import { useClientManager } from "@megaron/react-clients";
import { Serializers } from "@megaron/serializers";
import React from "react";

type CatalogItemSyncChannel =
  | "bcItem"
  | "sheetsItem"
  | "shopifyProduct"
  | "shopifyVariant"
  | "shopifyPrice"
  | "sheetsPriceList"
  | "sheetsPrice";
type DiffValue = Record<string, unknown>;

type Props<T extends DiffValue> = {
  title: string;
  channel: CatalogItemSyncChannel;
  directionLabels: {
    pull: string;
    push: string;
  };
  labels: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, (value: unknown) => React.ReactNode>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
};

export const BcItemSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<BcItem & DiffValue>
    title="Synchronizacja Business Central"
    channel="bcItem"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do BC",
    }}
    alwaysVisibleColumns={["itemName"]}
    labels={{
      bcId: "Id BC",
      itemId: "Id",
      itemName: "Nazwa",
      gtin: "EAN",
    }}
  />
);

export const SheetsItemSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<Item> & DiffValue>
    title="Synchronizacja Google Sheets"
    channel="sheetsItem"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do google sheets",
    }}
    alwaysVisibleColumns={["itemName"]}
    labels={itemLabels}
    formatters={itemFormatters}
  />
);

export const ShopifyProductSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<ShopifyProduct> & DiffValue>
    title="Synchronizacja Shopify - produkty"
    channel="shopifyProduct"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do Shopify",
    }}
    alwaysVisibleColumns={["handle", "title"]}
    labels={shopifyProductLabels}
  />
);

export const ShopifyVariantSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<ShopifyProductVariant> & DiffValue>
    title="Synchronizacja Shopify - warianty"
    channel="shopifyVariant"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do Shopify",
    }}
    alwaysVisibleColumns={["sku", "variant"]}
    labels={shopifyVariantLabels}
    formatters={shopifyVariantFormatters}
  />
);

export const ShopifyPriceSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<ShopifyPrice> & DiffValue>
    title="Synchronizacja Shopify - ceny"
    channel="shopifyPrice"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do Shopify",
    }}
    alwaysVisibleColumns={["itemId", "itemName"]}
    labels={shopifyPriceLabels}
    formatters={shopifyPriceFormatters}
  />
);

export const SheetsPriceListSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<PriceList & DiffValue>
    title="Synchronizacja Google Sheets - cenniki"
    channel="sheetsPriceList"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do google sheets",
    }}
    alwaysVisibleColumns={["name"]}
    labels={priceListLabels}
  />
);

export const SheetsPriceSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<PriceSheetRow> & DiffValue>
    title="Synchronizacja Google Sheets - ceny"
    channel="sheetsPrice"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do google sheets",
    }}
    alwaysVisibleColumns={["priceList", "itemId", "itemName", "activeFrom"]}
    labels={priceLabels}
    formatters={priceFormatters}
  />
);

const CatalogItemSyncDiffPage = <T extends DiffValue>(props: Props<T>) => {
  const client = useClientManager("catalog");

  const pullQuery = client.diffQuery().useQuery({
    channel: props.channel,
    isPullable: true,
  });

  const pushQuery = client.diffQuery().useQuery({
    channel: props.channel,
    isPushable: true,
  });

  const allDiffsQuery = client.diffQuery().useQuery({
    channel: props.channel,
  });
  const channelQuery = client.diffChannelQuery().useQuery({
    channel: props.channel,
  });

  const refreshMutation = client.refreshDiffs().useMutation();
  const pullMutation = client.pullDiffs().useMutation();
  const pushMutation = client.pushDiffs().useMutation();

  const refetchDiffs = async () => {
    await Promise.all([pullQuery.refetch(), pushQuery.refetch(), allDiffsQuery.refetch()]);
  };

  const refresh = async () => {
    await refreshMutation.mutateAsync({ channel: props.channel });
    await refetchDiffs();
  };

  const pull = async (diffIds: string[]) => {
    await pullMutation.mutateAsync({ channel: props.channel, diffIds });
    await refetchDiffs();
  };

  const push = async (diffIds: string[]) => {
    await pushMutation.mutateAsync({ channel: props.channel, diffIds });
    await refetchDiffs();
  };

  return (
    <DiffSyncPage<T>
      title={props.title}
      pullableDiffs={pullQuery.data as DiffSyncRecord<T>[] | undefined}
      pushableDiffs={pushQuery.data as DiffSyncRecord<T>[] | undefined}
      errorDiffs={allDiffsQuery.data?.filter(isErrorDiff) as DiffSyncRecord<T>[] | undefined}
      directionLabels={props.directionLabels}
      labels={props.labels}
      formatters={props.formatters}
      alwaysVisibleColumns={props.alwaysVisibleColumns}
      remoteResourceUrl={channelQuery.data?.remoteResourceUrl}
      isLoading={
        pullQuery.isLoading ||
        pushQuery.isLoading ||
        allDiffsQuery.isLoading ||
        pullQuery.isFetching ||
        pushQuery.isFetching ||
        allDiffsQuery.isFetching
      }
      isRefreshing={refreshMutation.isLoading}
      isExecuting={pullMutation.isLoading || pushMutation.isLoading}
      onRefresh={refresh}
      onPull={pull}
      onPush={push}
    />
  );
};

const isErrorDiff = (diff: DiffSyncRecord<DiffValue>) => !!diff.error;

const itemLabels = {
  bcId: "Id BC",
  shopifyProductId: "Id produktu Shopify",
  box: "Karton",
  brand: "Marka",
  cost: "Koszt STD",
  defaultProfit: "Domyślna marża",
  description: "Opis",
  gtin: "EAN",
  handle: "Handle",
  isArchived: "Zarchiwizowany?",
  isInAvailability: "Śledzenie dostępności",
  isLoyalty: "Produkt Specjalista",
  itemId: "Id",
  itemName: "Nazwa",
  layer: "Warstwa",
  massKg: "Masa kg",
  pallet: "Paleta",
  productGroup: "Grupa produktowa",
  productId: "Indeks produktu",
  size: "Wielkość",
  sizeUnit: "Jednostka sprzedaży",
  title: "Tytuł",
  variant: "Wariant",
  shopifyVariantId: "variantId",
} satisfies Partial<Record<keyof Item, string>>;

const itemFormatters = {
  variant: (value) => formatItemVariant(value as ItemVariant) || "-",
} satisfies Partial<Record<keyof Item, (value: unknown) => React.ReactNode>>;

const shopifyProductLabels = {
  id: "Id Shopify",
  title: "Tytuł",
  description: "Opis",
  vendor: "Marka",
  handle: "Handle",
  tags: "Tagi",
} satisfies Partial<Record<keyof ShopifyProduct, string>>;

const shopifyVariantLabels = {
  variantId: "Id wariantu",
  sku: "SKU",
  productId: "Id produktu",
  variant: "Wariant",
  barcode: "EAN",
  weightKg: "Masa kg",
} satisfies Partial<Record<keyof ShopifyProductVariant, string>>;

const shopifyVariantFormatters = {
  variant: (value) => formatItemVariant(value as ItemVariant) || "-",
} satisfies Partial<Record<keyof ShopifyProductVariant, (value: unknown) => React.ReactNode>>;

const shopifyPriceLabels = {
  itemId: "SKU",
  itemName: "Nazwa",
  price: "Cena",
} satisfies Partial<Record<keyof ShopifyPrice, string>>;

const shopifyPriceFormatters = {
  price: (value) => formatDecimalValue(value),
} satisfies Partial<Record<keyof ShopifyPrice, (value: unknown) => React.ReactNode>>;

const priceListLabels = {
  name: "Cennik",
  currency: "Waluta",
} satisfies Partial<Record<keyof PriceList, string>>;

const priceLabels = {
  itemId: "Produkt",
  itemName: "Nazwa",
  priceList: "Cennik",
  value: "Wartość",
  activeFrom: "Od",
} satisfies Partial<Record<keyof PriceSheetRow, string>>;

const priceFormatters = {
  activeFrom: (value) => formatDateValue(value),
  value: (value) => formatDecimalValue(value),
} satisfies Partial<Record<keyof PriceSheetRow, (value: unknown) => React.ReactNode>>;

const formatDateValue = (value: unknown) =>
  value instanceof Date ? Serializers.dateOnly.serialize(value) : value ? String(value) : "-";

const formatDecimalValue = (value: unknown) => (value === null || value === undefined ? "-" : value.toString());
